import React from 'react';
import { useSelector } from 'react-redux';
import { AlertInline, Link, Text, themeVars } from '@eds-web/react';
import { useTranslation } from 'react-i18next';
import moment from 'moment/moment';
import { Trans } from 'react-i18next';

import {
    selectAccountStatus,
    selectAccountStatusLoading,
    selectTaxProfileStatus,
    selectTaxProfileState
} from '../../selectors/tax.selector';
import { ACCOUNT_STATUS, TAX_PROFILE_STATUS } from '../../types/payout.types';
import { useTab } from '../../contexts/TabContext';
import { TabValue } from '../../constants/prize';
import { selectWinnings } from '../../selectors/prize.selector';

interface StatusMessage {
    code: string;
    tab?: TabValue;
}

const isTaxStatusRequireUpdate = (taxProfileStatus: TAX_PROFILE_STATUS): boolean => {
    const requiresUpdateStatuses: TAX_PROFILE_STATUS[] = [
        TAX_PROFILE_STATUS.NAME_MISS_MATCH,
        TAX_PROFILE_STATUS.INVALID_TAX_INFO,
        TAX_PROFILE_STATUS.MISSING_TAX_INFO,
        TAX_PROFILE_STATUS.DEACTIVATED,
        TAX_PROFILE_STATUS.TAX_INFO_REQUIRES_UPDATE
    ];
    return requiresUpdateStatuses.includes(taxProfileStatus);
};

const getTaxProfileStatusMessage = (
    taxProfileStatus?: TAX_PROFILE_STATUS,
    taxProfileState?: any
): StatusMessage | null => {
    if (!taxProfileStatus) return null;
    const statusMap: Record<TAX_PROFILE_STATUS, StatusMessage> = {
        [TAX_PROFILE_STATUS.NULL]: {
            code: 'epic.esport.profile.header.message.invalidated.v2',
            tab: TabValue.PRIZES
        }
    } as Record<TAX_PROFILE_STATUS, StatusMessage>;

    if (isTaxStatusRequireUpdate(taxProfileStatus)) {
        const hasRetakeAvailable = taxProfileState?.retakeAvailable;
        return {
            code: hasRetakeAvailable
                ? 'epic.esport.profile.header.message.requiresUpdate.v2'
                : 'epic.esport.profile.header.message.invalidated.v2',
            tab: hasRetakeAvailable ? TabValue.TAX : TabValue.PRIZES
        };
    }

    return statusMap[taxProfileStatus] || null;
};

const getAccountStatusMessage = (accountStatus?: string): string | null => {
    if (!accountStatus) return null;

    const accountStatusMap: Record<string, string> = {
        [ACCOUNT_STATUS.PENDING_ACCOUNT_ACTIVATION]: 'epic.esport.profile.account.active',
        [ACCOUNT_STATUS.ACCOUNT_PENDING_VERIFICATION]: 'epic.esport.profile.account.verification',
        [ACCOUNT_STATUS.PENDING_TRANSFER_METHOD_ACTION]: 'epic.esport.profile.account.transfer'
    };

    return accountStatusMap[accountStatus] || null;
};

const getTaxExpiredMessage = (taxStatusExpiryDate?: Date) => {
    if (!taxStatusExpiryDate) return null;
    const currentDate = moment();
    const expiryDate = moment(taxStatusExpiryDate);
    return expiryDate.isBefore(currentDate.add(3, 'months'));
};

export const NotificationAlert: React.FC = () => {
    const accountStatus = useSelector(selectAccountStatus);
    const loading = useSelector(selectAccountStatusLoading);
    const taxProfileStatus = useSelector(selectTaxProfileStatus);
    const taxProfileState = useSelector(selectTaxProfileState);
    const winnings = useSelector(selectWinnings);
    const { setCurrentTab } = useTab();
    const { t } = useTranslation();
    if (loading) {
        return null;
    }

    const taxProfileMessage = getTaxProfileStatusMessage(
        taxProfileStatus as TAX_PROFILE_STATUS,
        taxProfileState
    );
    const accountStatusMessage = getAccountStatusMessage(accountStatus);
    const taxExpireMessage = getTaxExpiredMessage(taxProfileState?.taxProfile?.taxStatusExpiryDate);
    const handleLinkClick = (tab: TabValue = TabValue.TAX) => {
        setCurrentTab(tab);
    };

    if ((taxProfileMessage || accountStatusMessage || taxExpireMessage) && winnings.length) {
        return (
            <AlertInline
                status="warning"
                message={
                    <>
                        {taxProfileMessage && (
                            <Text variant={'paragraphMD'}>
                                <Trans
                                    components={{
                                        a: (
                                            <Link
                                                style={{
                                                    color: themeVars.color.transparent.light['100']
                                                }}
                                                onClick={() =>
                                                    handleLinkClick(taxProfileMessage.tab)
                                                }
                                            />
                                        )
                                    }}
                                    i18nKey={taxProfileMessage.code}
                                />
                            </Text>
                        )}
                        {accountStatusMessage && (
                            <Text variant={'paragraphMD'}>{t(accountStatusMessage)}</Text>
                        )}
                        {taxExpireMessage &&
                            !isTaxStatusRequireUpdate(taxProfileStatus as TAX_PROFILE_STATUS) && (
                                <Text variant={'paragraphMD'}>
                                    <Trans
                                        components={{
                                            a: (
                                                <Link
                                                    style={{
                                                        color: themeVars.color.transparent.light[
                                                            '100'
                                                        ]
                                                    }}
                                                    onClick={() => handleLinkClick(TabValue.TAX)}
                                                />
                                            )
                                        }}
                                        i18nKey={'epic.esport.profile.tax.expire.message.v2'}
                                        values={{
                                            date: moment(
                                                taxProfileState?.taxProfile?.taxStatusExpiryDate
                                            ).format('MMM D, YYYY')
                                        }}
                                    />
                                </Text>
                            )}
                    </>
                }
            />
        );
    }
    return null;
};
